/*
  KAN-276 — AI Scent Builder client.

  Reads two JSON blobs rendered by sections/olf-ai-builder.liquid:
    [data-aib-config]  — endpoint URL, fee products, free-engraving promo, strings
    [data-aib-library] — cores (real custom-scent products) + variants (one per accord)

  Compose flow:
    - endpoint set  → POST {endpoint}/compose {prompt, recipientName}
                      expects {core, accordNumber|accord, blendName, family,
                               keyNotes[], tagline, story}
                      POST {endpoint}/note {prompt, blendName, family, keyNotes, from, to}
                      expects {note}
    - endpoint blank → deterministic local demo blend (clearly labeled in the UI)

  Cart contract mirrors the production customizer (src/App.jsx handleAddToCart):
    main item      = real custom-scent variant + visible/hidden line-item properties
    engraving fee  = child line item (_is_child, _is_engraving_fee), promo-aware
    intense        = child line item (_is_child, _is_enhanced_scent)
*/
(function () {
  'use strict';

  if (customElements.get('olf-ai-builder')) return;

  var DEMO_NAMES = [
    'Archive', 'Solstice', 'Harbor', 'Vesper', 'Meridian', 'Reverie',
    'Fable', 'Cadence', 'Lumen', 'Atlas', 'Sonder', 'Aurelia',
    'Driftwood', 'Ember', 'Juniper', 'Marginalia', 'Nocturne', 'Overture',
    'Praline', 'Quietude', 'Saltair', 'Tesoro', 'Umbra', 'Wildberry'
  ];
  var DEMO_TAGLINES = [
    'A bespoke composition built for them and them alone.',
    'One nose, one bottle — nobody else’s.',
    'A scent that didn’t exist until they did.'
  ];

  function hashString(str) {
    var h = 0;
    for (var i = 0; i < str.length; i++) h = ((h * 31) + str.charCodeAt(i)) >>> 0;
    return h;
  }

  function fmtMoney(cents) {
    var v = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + v;
  }

  function parseJSONScript(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function accordNumberFromSku(sku) {
    // SKU pattern: FRA-CUS-LUL-01-02-50 → segment 4 (0-indexed) is the accord number.
    if (!sku) return null;
    var parts = String(sku).split('-');
    if (parts.length < 6) return null;
    var n = parseInt(parts[4], 10);
    return isNaN(n) ? null : n;
  }

  function hexDist(a, b) {
    function rgb(h) {
      h = String(h || '').replace('#', '');
      if (h.length === 3) h = h.replace(/./g, function (c) { return c + c; });
      var n = parseInt(h, 16);
      return isNaN(n) ? [0, 0, 0] : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    var x = rgb(a), y = rgb(b);
    return Math.abs(x[0] - y[0]) + Math.abs(x[1] - y[1]) + Math.abs(x[2] - y[2]);
  }

  // The Gift Bottle controls use a nine-character cap in both modes. Labels
  // allow letters, numbers, spaces and periods; engraving allows printable ASCII.
  // Generated defaults and restored sessions need the same rule as typed input.
  function normalizeBottleName(value, mode) {
    var disallowed = mode === 'engrave' ? /[^\x20-\x7E]/g : /[^A-Za-z0-9 .]/g;
    return String(value || '').replace(disallowed, '').slice(0, 9);
  }

  // Fit a note inside the field's character cap without cutting a word -- or, where it
  // can, a sentence -- in half. The note is written by the service and assigned to
  // .value, which `maxlength` never constrains, so a hard slice(0, limit) left every
  // draft ending mid-word once the brand lowered the cap to 100 ("...organic nature of
  // y"). Prefer the last complete sentence; failing that, the last whole word.
  function fitNote(text, limit) {
    var s = String(text == null ? '' : text).trim();
    if (!limit || s.length <= limit) return s;
    var cut = s.slice(0, limit);
    var sentence = cut.match(/^[\s\S]*[.!?][\u201D\u2019"')]?(?=\s|$)/);
    if (sentence) return sentence[0].trim();
    var space = cut.lastIndexOf(' ');
    var words = (space > 0 ? cut.slice(0, space) : cut).replace(/[\s,;:\u2014\u2013-]+$/, '');
    return words.slice(0, limit - 1) + '\u2026';
  }

  var OlfAiBuilder = function () {
    return Reflect.construct(HTMLElement, [], OlfAiBuilder);
  };
  // Minimum time the filling-bottle loader stays up so its rise animation
  // (2.4s) can play even when the response is instant (demo/cached).
  OlfAiBuilder.MIN_COMPOSE_MS = 2600;
  OlfAiBuilder.prototype = Object.create(HTMLElement.prototype, {
    constructor: { value: OlfAiBuilder }
  });

  OlfAiBuilder.prototype.connectedCallback = function () {
    this.config = parseJSONScript(this, '[data-aib-config]') || {};
    this.library = parseJSONScript(this, '[data-aib-library]') || { cores: [] };
    this.state = {
      blend: null,          // {core, variant, name, family, keyNotes, tagline, story, demo}
      mode: 'label',        // 'label' | 'engrave'
      intense: false,
      colorName: '',
      colorHex: '',
      note: '',
      added: false,
      composing: false,
      noteWriting: false,
      adding: false
    };
    this.els = {};
    this.allEls = {};
    var self = this;
    this.querySelectorAll('[data-aib]').forEach(function (el) {
      var k = el.getAttribute('data-aib');
      self.els[k] = el;
      (self.allEls[k] = self.allEls[k] || []).push(el);
    });
    this.bind();

    /*
      Restore before anything else paints, so a refresh looks like the page never
      reloaded rather than flashing an empty form first. Saving is bound to the
      section as a whole: every input, toggle and swatch bubbles to here, so a new
      control is covered without anyone remembering to wire it up.
    */
    this.restoreSession();
    var self = this;

    /*
      From page load, not after a blend is composed.

      This used to be registered inside presentBlend, so before the first compose
      nothing listened at all: a customer who tapped TELL THEIR STORY on a fresh
      page got the keyboard AND every floating widget on top of it. It looked
      intermittent because it depended on timing -- tap before the widgets finished
      loading and they appeared over the keyboard anyway, since the class that hides
      them was never set.

      Typing is typing, whether a blend exists yet or not.
    */
    this.watchKeyboard();
    var saveSoon = null;
    var queueSave = function () {
      if (saveSoon) clearTimeout(saveSoon);
      saveSoon = setTimeout(function () { self.saveSession(); }, 250);
    };
    this.addEventListener('input', queueSave);
    this.addEventListener('change', queueSave);
    this.addEventListener('click', queueSave);
    this.applyFees();
  };

  OlfAiBuilder.prototype.el = function (k) { return this.els[k] || null; };
  // Every element carrying a hook, in document order. `el` keeps only the last one, which
  // suits a hook used once and is wrong for Add to Cart: the button under step 3 and the
  // one in the sticky bar are the same control and have to bind, disable and swap as one.
  OlfAiBuilder.prototype.all = function (k) { return this.allEls[k] || []; };

  OlfAiBuilder.prototype.syncBottleName = function () {
    var input = this.el('bottleNameInput');
    if (!input) return '';
    var normalized = normalizeBottleName(input.value, this.state.mode);
    if (input.value !== normalized) {
      var caret = input.selectionStart;
      var nextCaret = typeof caret === 'number'
        ? normalizeBottleName(input.value.slice(0, caret), this.state.mode).length
        : null;
      input.value = normalized;
      if (nextCaret !== null && document.activeElement === input) input.setSelectionRange(nextCaret, nextCaret);
    }
    input.setCustomValidity('');
    return normalized;
  };

  OlfAiBuilder.prototype.bind = function () {
    var self = this;

    this.querySelectorAll('[data-aib-example]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (self.el('story')) self.el('story').value = chip.getAttribute('data-aib-example');
        self.el('story').focus();
      });
    });

    if (this.el('compose')) this.el('compose').addEventListener('click', function () { self.compose(); });
    if (this.el('rewrite')) this.el('rewrite').addEventListener('click', function () { self.writeNote(); });
    this.all('startOver').forEach(function (btn) {
      btn.addEventListener('click', function () { self.startOver(); });
    });
    this.bindStartOverAsk();
    this.all('addToCart').forEach(function (btn) { btn.addEventListener('click', function () { self.addToCart(btn); }); });

    if (this.el('modeLabel')) this.el('modeLabel').addEventListener('click', function () { self.setMode('label'); });
    if (this.el('modeEngrave')) this.el('modeEngrave').addEventListener('click', function () { self.setMode('engrave'); });
    if (this.el('intenseToggle')) this.el('intenseToggle').addEventListener('click', function () { self.setIntense(!self.state.intense); });

    this.querySelectorAll('[data-aib-color]').forEach(function (sw) {
      sw.addEventListener('click', function () {
        self.setColor(sw.getAttribute('data-aib-color'), sw.getAttribute('data-aib-hex'));
      });
    });

    if (this.el('bottleNameInput')) this.el('bottleNameInput').addEventListener('input', function () {
      self.syncBottleName();
      self.renderBottle();
    });
    if (this.el('noteText')) this.el('noteText').addEventListener('input', function () {
      self.state.note = self.el('noteText').value;
      self.renderNoteCount();
      self.autoSize(self.el('noteText'));
    });
    if (this.el('story')) this.el('story').addEventListener('input', function () {
      self.autoSize(self.el('story'));
    });
    this.autoSize(this.el('story'));       // and on load, for a restored story
    // Poppins arriving after first paint changes how many lines the text wraps to
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.autoSizeNote(); });
    /*
      Mirror "who's it for" into the note's To field until the customer edits To
      themselves. The guard used to be `!toName.value`, which is only true on the
      FIRST keystroke: typing "Jeff" copied "J" and then never caught up, because
      every later keystroke found To non-empty and skipped. Track the edit
      explicitly instead. Clearing To resumes mirroring, so there is a way back.
    */
    if (this.el('recipient')) this.el('recipient').addEventListener('input', function () {
      if (self.el('toName') && !self.toNameEdited) self.el('toName').value = self.el('recipient').value;
      self.renderLetter();
    });
    if (this.el('toName')) this.el('toName').addEventListener('input', function () {
      self.toNameEdited = self.el('toName').value.trim() !== '';
      self.renderLetter();
    });
    if (this.el('fromName')) this.el('fromName').addEventListener('input', function () { self.renderLetter(); });
    // Typed in = theirs: To and From never overwrite it again. Clearing the line hands it
    // back to the automatic text, the same rule the To field follows.
    if (this.el('noteGreeting')) this.el('noteGreeting').addEventListener('input', function () {
      self.greetEdited = self.el('noteGreeting').value.trim() !== '';
      if (!self.greetEdited) self.renderLetter(); else self.renderNoteCount();
    });
    if (this.el('noteSignoff')) this.el('noteSignoff').addEventListener('input', function () {
      self.signEdited = self.el('noteSignoff').value.trim() !== '';
      if (!self.signEdited) self.renderLetter(); else { self.sizeSignoff(); self.renderNoteCount(); }
    });
    // KAN-839 -- the line after "Yours," is where they sign, so clicking it should put the
    // cursor in the sign-off. It is a drawn line, not a field, and clicks on it did nothing.
    var signRow = this.el('noteSignBlank') && this.el('noteSignBlank').parentNode;
    if (signRow && this.el('noteSignoff')) signRow.addEventListener('click', function (e) {
      var sign = self.el('noteSignoff');
      if (e.target === sign) return;
      sign.focus();
      var end = sign.value.length;
      try { sign.setSelectionRange(end, end); } catch (err) { /* not a text input */ }
    });
    if (this.el('recipientEmail')) this.el('recipientEmail').addEventListener('input', function () {
      self.el('recipientEmail').setCustomValidity('');
    });
    this.renderLetter();
  };

  /* ---------- the note as a letter (KAN-813) ----------

     Hannah Ellingson, KAN-276 comment 15956: the note should start "Dear [XX], I made
     this scent for you because..." and always end "Yours, [name]", with the From name
     filled in when there is one and a space to sign by hand when there is not.

     The greeting and sign-off are their own fields around the textarea. Each fills itself
     from To or From until the shopper types in it; after that it is theirs, and To and From
     never overwrite it (Cooper, 8 October: "I should still be able to edit the 'Yours,
     Cooper' part"; typing in From must not undo what they wrote). Clearing the line hands it
     back to the automatic text. The opening words are in the textarea: the AI writes the note
     to start with them, and the shopper may still reword the sentence. */
  OlfAiBuilder.prototype.autoLetter = function () {
    var l = this.config.letter || {};
    var to = (this.el('toName') && this.el('toName').value || '').trim();
    var from = (this.el('fromName') && this.el('fromName').value || '').trim();
    return {
      greeting: to && l.greeting ? l.greeting + ' ' + to + ',' : '',
      greetingHint: l.greeting ? l.greeting + ' \u2026,' : '',
      signoff: l.signoff ? l.signoff + (from ? ' ' + from : '') : '',
      from: from
    };
  };

  OlfAiBuilder.prototype.renderLetter = function () {
    var a = this.autoLetter();
    var greet = this.el('noteGreeting');
    if (greet) {
      greet.placeholder = a.greetingHint;
      if (!this.greetEdited) greet.value = a.greeting;
    }
    var sign = this.el('noteSignoff');
    if (sign && !this.signEdited) sign.value = a.signoff;
    this.sizeSignoff();
    this.renderNoteCount();   // To and From change the greeting and sign-off, so the card's length
  };

  // The sign-off sits on one line with the line to sign after it, so it is only as wide as
  // its text; `ch` is the width of a zero, close enough for a short line of Poppins.
  OlfAiBuilder.prototype.sizeSignoff = function () {
    var sign = this.el('noteSignoff');
    var blank = this.el('noteSignBlank');
    if (!sign) return;
    sign.style.width = Math.max(3, sign.value.length + 1) + 'ch';
    // No From name and an untouched "Yours,": leave a line to sign by hand.
    if (blank) blank.hidden = !!(this.signEdited || this.autoLetter().from || !sign.value.trim());
  };

  /* The whole card as it should be hand-written: greeting, note, sign-off. With no
     From name the sign-off still ends the letter, so the studio leaves room to sign. */
  OlfAiBuilder.prototype.letterText = function () {
    var body = (this.el('noteText') && this.el('noteText').value || '').trim();
    if (!body) return '';
    var greet = (this.el('noteGreeting') && this.el('noteGreeting').value || '').trim();
    var sign = (this.el('noteSignoff') && this.el('noteSignoff').value || '').trim();
    return [greet, body, sign].filter(Boolean).join('\n');
  };

  /* ---------- fees / promo ---------- */

  OlfAiBuilder.prototype.promoActive = function () {
    var p = this.config.freeEngravingPromo || {};
    if (!p.enabled) return false;
    if (p.forceActive) return true;
    var now = Date.now();
    var start = Date.parse(p.startAt || '') || 0;
    var end = Date.parse(p.endAt || '') || 0;
    return now >= start && now < end;
  };

  OlfAiBuilder.prototype.engraveFeeCents = function () {
    if (this.promoActive()) return 0;
    return (this.config.engrave && this.config.engrave.price) || 0;
  };

  OlfAiBuilder.prototype.applyFees = function () {
    var engrave = this.config.engrave || {};
    var intense = this.config.intense || {};
    // Engrave option only offered when the fee product is configured & available
    if (this.el('modeEngrave')) {
      if (engrave.variantId && engrave.available) {
        this.el('modeEngrave').hidden = false;
        if (this.el('engraveFee')) {
          this.el('engraveFee').textContent = this.engraveFeeCents() === 0
            ? (this.config.strings && this.config.strings.feeFree) || 'Free'
            : '+' + fmtMoney(this.engraveFeeCents());
        }
      } else {
        this.el('modeEngrave').hidden = true;
      }
    }
    if (this.el('intenseWrap')) {
      // `enabled` is the brand's kill switch (wizard.show_enhance_scent on the
      // Custom Scent 50ml product), the same one the customizer honours. Only an
      // explicit false hides the toggle — an absent flag arrives as undefined and
      // leaves it visible, so a config slip cannot silently drop a paid upgrade.
      if (intense.enabled !== false && intense.variantId && intense.available) {
        this.el('intenseWrap').hidden = false;
        if (this.el('intenseFee')) this.el('intenseFee').textContent = '+' + fmtMoney(intense.price || 0);
      } else {
        this.el('intenseWrap').hidden = true;
      }
    }
  };

  /* ---------- compose ---------- */

  OlfAiBuilder.prototype.setComposing = function (on) {
    this.state.composing = on;
    if (this.el('compose')) this.el('compose').disabled = on;
    if (this.el('composeIdle')) this.el('composeIdle').hidden = on;
    if (this.el('composeBusy')) this.el('composeBusy').hidden = !on;
    // The filling-bottle animation replays each time the section is (re)shown
    // because it is removed from the DOM flow while hidden.
    if (this.el('loading')) {
      this.el('loading').hidden = !on;
      if (on && this.el('loading').scrollIntoView) {
        this.el('loading').scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  OlfAiBuilder.prototype.compose = function () {
    var self = this;
    var story = (this.el('story') && this.el('story').value || '').trim();
    if (!story || this.state.composing) return;
    var recipient = (this.el('recipient') && this.el('recipient').value || '').trim();

    this.state.attempt = (this.state.attempt || 0) + 1;
    this.setComposing(true);
    if (this.el('composeError')) this.el('composeError').hidden = true;

    var endpoint = (this.config.endpoint || '').trim();
    var run = endpoint
      ? this.remoteCompose(endpoint, story, recipient)
      : Promise.resolve(this.demoCompose(story));

    // Hold the reveal until the bottle-fill animation has had time to play, so a
    // fast/instant (demo or cached) response doesn't skip past it. Network time
    // on the real endpoint usually already exceeds this.
    var startedAt = Date.now();
    run.then(function (blend) {
      if (!blend) throw new Error('no blend');
      var wait = Math.max(0, OlfAiBuilder.MIN_COMPOSE_MS - (Date.now() - startedAt));
      setTimeout(function () {
        self.state.blend = blend;
        self.state.added = false;
        // KAN-774 -- a blend composed after one has already been added starts a new order
        // line: bring both Add to Cart buttons back and hide both "Added" pills. Before,
        // the buttons stayed hidden for the rest of the visit and a second blend showed
        // "Added to cart" with nothing to press.
        self.all('addToCart').forEach(function (btn) { btn.hidden = false; });
        self.all('addedState').forEach(function (n) { n.hidden = true; });
        self.setComposing(false);
        // Same slot serves the demo notice and the load-shed notice — both say
        // "this is not the real thing", which is the only honest framing for either.
        var notice = self.el('demoNote');
        if (notice) {
          if (blend.degraded && blend.degradedMessage) notice.textContent = blend.degradedMessage;
          notice.hidden = !(blend.demo || blend.degraded);
        }
        self.presentBlend();
        self.writeNote();
      }, wait);
    }).catch(function (err) {
      self.setComposing(false);
      /*
        A sparse story is not a failure. The customer wrote too little and is being
        asked for more, which is the flow working -- reporting it would fill the
        stream with our own copy being read.
      */
      if (!(err && err.sparse)) self.reportFailure('compose', err);
      if (self.el('composeError')) {
        self.el('composeError').textContent = err && err.sparse
          ? self.sparseMessage(err)
          : ((self.config.strings && self.config.strings.genericError) || 'Something went wrong — please try again.');
        self.el('composeError').hidden = false;
      }
    });
  };

  /*
    Copy for a too-thin story. The lead sentence is overridable from the section
    settings if the brand wants to word it themselves; the hints come from the
    service, which knows which details were actually missing.
  */
  OlfAiBuilder.prototype.sparseMessage = function (err) {
    var strings = this.config.strings || {};
    var lead = err && err.playful
      ? (strings.playfulStory || 'We might be missing the joke — tell us about the real person and we\'ll make them something good.')
      : (strings.sparseStory || 'Tell us a little more about them and we\'ll build something better.');
    /*
      Drop a hint that repeats the lead. The service's joke reply opened with "Tell us
      about the real person and we will make them something good", which the playful
      lead already says, so the customer read it twice (KAN-839).
    */
    var key = function (s) {
      return String(s).toLowerCase().replace(/we'll/g, 'we will').replace(/[^a-z ]+/g, ' ').replace(/\s+/g, ' ').trim();
    };
    var leadKey = key(lead);
    var qs = ((err && err.questions) || []).filter(function (q) {
      return key(q) && leadKey.indexOf(key(q)) === -1;
    });
    return qs.length ? lead + ' ' + qs.join(' ') : lead;
  };

  /*
    Per-VISIT id for regeneration tracking (KAN-446).

    sessionStorage, deliberately — it dies with the tab. localStorage would make
    this a persistent cross-visit identifier, which is exactly what the retention
    design set out to avoid: the compositions table holds no IP and nothing that
    would let it become a tracking profile.
  */
  OlfAiBuilder.prototype.sessionId = function () {
    try {
      var k = 'olf-ai-session';
      var v = window.sessionStorage.getItem(k);
      if (!v) {
        v = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : String(Date.now()) + '-' + Math.random().toString(36).slice(2, 10);
        window.sessionStorage.setItem(k, v);
      }
      return v;
    } catch (e) {
      return ''; // private mode or storage disabled — tracking is optional, composing is not
    }
  };

  /*
    Survive a refresh (KAN-536).

    Before this, refreshing or navigating away lost the blend, the personalisation
    and the note, with no warning. Recomposing costs another AI call and returns a
    DIFFERENT blend, so the work was not recoverable even by repeating the steps.

    sessionStorage, matching sessionId() above: it dies with the tab, so this does
    not become a cross-visit record of what someone was buying. A customer who
    closes the tab still starts fresh, which is the intended behaviour; only the
    accidental refresh is covered.

    The payload is versioned. A stored shape from an older deploy is discarded
    rather than half-applied, because a partial restore is harder to notice than
    an empty form.
  */
  var SESSION_KEY = 'olf-ai-progress';
  var SESSION_VERSION = 1;

  /*
    Sit the third-party widgets on one centre line above the sticky bar (KAN-544).

    They are injected outside this section by their own apps, each anchoring itself
    to the viewport bottom at a different height, so they landed on four different
    lines and overlapped the page content. Lifting them with CSS was not enough:
    aligning them needs each one's measured height, and UserWay writes
    `bottom: !important` inline, which no stylesheet can outrank.

    `bottom` is the lever. Each of these sets its own inline, which a stylesheet
    cannot outrank but a script write can, and the observer in applyShift puts ours
    back whenever a vendor overwrites it.
  */
  var COMPANION_SELECTORS = [
    '.userway_buttons_wrapper',
    '#OlfSmsChatCompanion',
    '#chat-button',
    '#mount-floating-engagement-widget',
    'div[class*="kl-teaser"]',
    '.salepify-popup-wrapper'
  ];

  /*
    Hide the floating widgets while the on-screen keyboard is up.

    With the keyboard open the usable height collapses to a few hundred pixels, and
    the widgets keep their offset from the bottom of it — so they land on top of the
    cart bar and, in one capture, directly over ADD TO CART. None of them is any use
    to someone who is mid-sentence in a text field.

    visualViewport is the only thing that reports the keyboard. window.innerHeight
    does not change on iOS when it opens, which is why a resize listener alone will
    not catch this.
  */
  OlfAiBuilder.prototype.watchKeyboard = function () {
    if (this._keyboardWatched) return;
    this._keyboardWatched = true;
    var self = this;

    /*
      FOCUS is the reliable signal, not viewport geometry.

      Inferring the keyboard from visualViewport.height was wrong twice over. It
      failed on a real iPhone, and it cannot even be tested through iPhone
      Mirroring: that uses the Mac's hardware keyboard, so iOS never raises the
      software one and the viewport barely moves. A heuristic that cannot be
      exercised is a heuristic nobody can trust.

      On a touch device, a focused text field means a keyboard. That is directly
      observable, fires before the animation rather than during it, and is testable
      anywhere focus works.

      Gated on a coarse pointer so focusing a field on a desktop, where there is no
      keyboard taking up the screen, does not hide anything.
    */
    var touchLike = function () {
      return window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
    };
    var setOpen = function (open) {
      document.documentElement.classList.toggle('olf-aib-keyboard-open', open);
      // The enforcement pass rides the reconciler, which otherwise only starts once
      // something has been repositioned -- and the reported case is a keyboard that
      // opens BEFORE any widget exists to reposition.
      if (open) startReconciler(); else self.positionCompanions(true, 'keyboard closed');
    };
    this._setKeyboardOpen = setOpen;
    // Safari fires this when a dismissed keyboard restores the page
    window.addEventListener('pageshow', function () { setOpen(false); });
    this.addEventListener('focusin', function (e) {
      var el = e.target;
      if (!el || !/^(INPUT|TEXTAREA)$/.test(el.tagName)) return;
      if (!touchLike()) return;
      setOpen(true);
    });
    this.addEventListener('focusout', function () {
      // let focus land on the next field before deciding the keyboard has gone
      setTimeout(function () {
        var a = document.activeElement;
        var stillTyping = a && /^(INPUT|TEXTAREA)$/.test(a.tagName) && self.contains(a);
        if (!stillTyping) setOpen(false);
      }, 80);
    });

    /*
      visualViewport, measured against the LAYOUT viewport.

      The previous test was `vv.height < window.innerHeight * 0.75`, and it never
      fired once. On Chrome for iOS `window.innerHeight` tracks the VISUAL viewport
      -- it shrinks along with the keyboard -- so the ratio stays near 1 and the
      keyboard is never detected. The class was therefore only ever set by focusin
      and only ever cleared by focusout.

      That is precisely the reported bug: dismissing the keyboard with Chrome's own
      dismiss button does not blur the field, so focusout never fires, and the
      widgets stayed hidden until the customer tapped the page -- the tap being the
      blur. Reported from a real device on 2026-08-27, after I had wrongly written
      it off as an artefact of iPhone Mirroring's hardware keyboard.

      documentElement.clientHeight is the layout viewport and does NOT shrink for
      the keyboard, so it is a stable thing to measure against. The largest viewport
      height seen while closed is carried too, in case a browser does shrink it.

      An absolute threshold rather than a ratio: a keyboard takes 250-350px, a URL
      bar 50-90px, and 150px separates them on any phone without needing to know
      the screen size.
    */
    var vv = window.visualViewport;
    if (!vv) return;

    var KEYBOARD_MIN_SHRINK = 150;
    var maxOpenHeight = 0;
    var wasOpen = null;
    var apply = function () {
      var baseline = Math.max(document.documentElement.clientHeight || 0, maxOpenHeight);
      if (!baseline) return;
      var open = (baseline - vv.height) > KEYBOARD_MIN_SHRINK;
      if (!open) maxOpenHeight = Math.max(maxOpenHeight, vv.height);
      if (open === wasOpen) return;          // nothing changed, do no work
      wasOpen = open;
      /*
        Both directions, deliberately.

        Making this add-only left the widgets hidden after the keyboard was
        dismissed, until the customer tapped the page. On iOS, dismissing the
        keyboard does not necessarily blur the field -- it can stay focused with the
        keyboard gone -- so focusout never fires and nothing clears the class. The
        tap that appeared to fix it was the blur.

        The viewport returning to full height means the keyboard has gone, whatever
        focus is doing.
      */
      if (open) document.documentElement.classList.add('olf-aib-keyboard-open');
      else self._setKeyboardOpen(false);
    };
    /*
      resize only. visualViewport ALSO fires `scroll` continuously while the page
      scrolls on iOS, and repositioning from that handler re-measured and
      re-transformed every widget on every frame -- which is exactly the jitter
      reported on device. Scrolling does not change whether the keyboard is up.
    */
    vv.addEventListener('resize', apply);
    apply();
  };

  /*
    On-device diagnostics, off unless ?olfdebug=1 is on the URL.

    A phone has no console anyone can read mid-scroll, so this writes to a small
    fixed panel: how many reposition passes have run, how many were skipped by the
    guard, and what triggered the last one. If the widgets shift again, the counter
    moving while you scroll says it is us; the counter staying still says it is not.
  */
  OlfAiBuilder.prototype.debugOn = function () {
    if (this._debugOn === undefined) {
      this._debugOn = /[?&]olfdebug=1/.test(window.location.search);
    }
    return this._debugOn;
  };

  OlfAiBuilder.prototype.debugLog = function (reason, applied) {
    if (!this.debugOn()) return;
    this._dbgRuns = (this._dbgRuns || 0) + 1;
    if (!applied) this._dbgSkipped = (this._dbgSkipped || 0) + 1;
    var panel = this._dbgPanel;
    if (!panel) {
      panel = document.createElement('div');
      panel.style.cssText = 'position:fixed;left:4px;top:4px;z-index:2147483647;'
        + 'background:rgba(0,0,0,.82);color:#0f0;font:11px/1.35 ui-monospace,monospace;'
        + 'padding:5px 7px;border-radius:4px;pointer-events:none;white-space:pre;';
      document.body.appendChild(panel);
      this._dbgPanel = panel;
      var self = this;
      setInterval(function () { self.debugRender(); }, 250);
    }
    this._dbgLast = reason + (applied ? ' APPLIED' : ' (no change)');
    this.debugRender();
  };

  /*
    The panel used to redraw only on a reposition pass, which is precisely what it
    was being used to rule out -- so during a scroll it sat frozen on its last line
    and looked like nothing was happening. What moves the widgets on a phone is the
    vendors rewriting `bottom`, which happens in the observer and not here, so the
    panel has to redraw on its own clock and report those restores.
  */
  OlfAiBuilder.prototype.debugRender = function () {
    var panel = this._dbgPanel;
    if (!panel) return;
    var line = '';
    COMPANION_SELECTORS.forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el || el.__olfShiftTotal === undefined) return;
      var r = el.getBoundingClientRect();
      var name = sel.replace(/[^a-z]/gi, '').slice(0, 4);
      /*
        A hidden element reports a ZERO rect, and the centre maths then returns the
        viewport height -- a plausible-looking number that means nothing. Read live
        on device with the keyboard up, all five showed c662 and it took a second
        look to realise that was the panel lying, not the widgets flying up the page.
      */
      var hidden = !r.width && !r.height;
      line += '\n' + name + ' '
            + (hidden ? 'hid ' : 'c' + Math.round(window.innerHeight - (r.top + r.height / 2)) + ' ')
            + 'r' + el.__olfShiftTotal;
    });
    panel.textContent =
      'passes ' + this._dbgRuns + '  skipped ' + (this._dbgSkipped || 0)
      + '\nlast: ' + (this._dbgLast || '-')
      + '\nscrollY ' + Math.round(window.scrollY)
      + line;
  };

  OlfAiBuilder.prototype.positionCompanions = function (force, reason) {
    var bar = this.el('bar');
    if (!bar || bar.hidden) return;
    var barH = bar.offsetHeight;
    if (!barH) return;

    /*
      Do nothing unless the inputs to the calculation actually changed.

      On iOS the URL bar collapses and expands as you scroll, firing resize
      repeatedly, and every one of those used to re-measure and re-transform all
      five widgets. The result was visible jitter the whole time you were scrolling.
      The widgets are position:fixed, so scrolling does not move them and there is
      nothing to recompute.

      innerHeight is deliberately NOT in the signature: it changes with the URL bar
      on every scroll, and the widgets are anchored to the visual viewport bottom,
      so that change does not affect where they belong.
    */
    var signature = window.innerWidth + 'x' + barH;
    if (!force && signature === this._companionSignature) {
      this.debugLog(reason || 'unnamed', false);
      return;
    }
    this._companionSignature = signature;
    this.debugLog(reason || 'unnamed', true);

    var found = [];
    COMPANION_SELECTORS.forEach(function (sel) {
      Array.prototype.forEach.call(document.querySelectorAll(sel), function (el) {
        // Measured as-is: nothing has to move in order to be measured.
        var r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          found.push({ el: el, rect: r, applied: el.__olfShiftY || 0 });
        }
      });
    });
    if (!found.length) return;

    /*
      Split them. Some of these are floating buttons; others, like the promo
      wrappers, are full-viewport overlays whose only visible child sits at the
      bottom edge. Centring an overlay is meaningless, and letting one into the
      tallest calculation puts the line halfway up the screen -- with a
      390x844 overlay in the set the line landed at 510px instead of 130px.
    */
    var BUTTON_MAX = 120;
    var buttons = found.filter(function (f) { return f.rect.height <= BUTTON_MAX; });
    var overlays = found.filter(function (f) { return f.rect.height > BUTTON_MAX; });

    // Overlays: lift by the bar, nothing more.
    overlays.forEach(function (f) {
      applyShift(f.el, barH + 12);
    });

    if (!buttons.length) return;

    // Buttons: one centre line, clear of the bar by 12px, with room for the tallest.
    var tallest = buttons.reduce(function (m, f) { return Math.max(m, f.rect.height); }, 0);
    var centreFromBottom = barH + 12 + tallest / 2;

    buttons.forEach(function (f) {
      // the measured centre already includes whatever we applied last time
      /*
        Correct RELATIVE to where it actually is, not from an assumed relationship
        between `bottom` and the visible centre.

        Assuming centre = bottom + height/2 put three of the four on the line and
        the Klaviyo teaser 20px above it: its rect is not positioned directly by its
        own `bottom`, because something inside it is offset. Measuring the residual
        and adjusting by it works whatever a vendor does internally.
      */
      var measuredCentre = window.innerHeight - (f.rect.top + f.rect.height / 2);
      var residual = centreFromBottom - measuredCentre;
      if (Math.abs(residual) < 1) return;             // already where it belongs
      applyShift(f.el, f.applied + residual);
    });
  };

  /*
    UserWay does not merely set its own position, it RE-SETS it: our transform
    lands, and a moment later the widget writes `transform: none !important` back
    over it. Watched over 8 seconds, the button moved to the right place twice and
    was pulled back down onto the cart bar both times.

    So the value has to be re-applied when the vendor clears it. The observer is
    scoped to that one element's style attribute and only acts when the value is
    not the one we set.
  */

  /*
    Lift each widget by its own `bottom`, with no transform anywhere.

    Every earlier version transformed something -- first the widget, then a wrapper
    around it. On-device instrumentation settled which layer was at fault: during a
    scroll the guard reported `last: resize (no change)` with 8 of 13 passes
    skipped, so nothing was repositioning them and the movement was not ours.

    What was left was the transform itself. A position:fixed element inside a
    transformed ancestor is not reliably pinned during momentum scrolling on iOS
    Safari, and the wrapper only contained these widgets BECAUSE it had a transform.
    The thing that made the wrapper work was the thing making them drift.

    So: no wrapper, no transform. `bottom` moves a fixed element without involving
    the compositor.

    The vendors set their own `bottom` inline with !important, which a stylesheet
    cannot outrank -- but an inline write from script can, and the observer restores
    it if one of them overwrites. UserWay actively normalises `transform`, which is
    what defeated the direct approach before; it sets `bottom` once at init and
    leaves it alone.

    Removing the wrapper also drops the iframe special case: nothing is re-parented,
    so the chat widget can no longer be reloaded by a reposition.
  */
  /*
    The cap on corrections is per BURST, not per session.

    It was a lifetime budget of 30, and disconnected the observer for good once
    spent. On a desktop browser the vendors never rewrite `bottom`, so the count sat
    at 0 or 1 and the cap was never reached -- which is why no amount of measuring
    here found anything. On a phone the iOS URL bar collapses and expands as you
    scroll, which fires the vendors' own reposition handlers, which rewrite `bottom`.
    Thirty of those go by in a few seconds of scrolling, and from then on the widget
    was released to sit wherever the vendor put it, for the rest of the visit.

    The cap is worth keeping for what it was written for: a vendor that fights back
    inside the same frame would otherwise spin here forever. That is a burst, and a
    burst ends in silence. So count corrections within a window and let a quiet gap
    reset the count, and back off rather than disconnecting -- backing off costs one
    scroll's worth of misplacement, where disconnecting costs the whole session.
  */
  var MAX_KEY_NOTES = 6;          // KAN-632; the service caps at the same number
  var SHIFT_BURST_LIMIT = 12;       // corrections allowed inside one burst
  var SHIFT_BURST_QUIET_MS = 400;   // silence that ends a burst
  var RECONCILE_MS = 250;           // how often we re-check, regardless of mutations

  /*
    A MutationObserver is not enough on its own, and the reason is easy to miss.

    It only ever hears about CHANGES. Once we stop correcting -- because a burst hit
    its cap -- the vendor's value stands, and the vendor's next write sets the same
    value again. Same value means no mutation record, which means the observer is
    never called, which means we never get another chance. Simulated as a phone
    does it, 120 rewrites in bursts, the widget dropped from the centre line to
    25px and stayed there while the observer sat waiting to be told.

    So the observer is the fast path, and this is the one that actually guarantees
    recovery: it asks what the value IS rather than waiting to be told it changed.
    Four string comparisons every quarter second.
  */
  var shifted = [];
  var reconciler = null;

  /*
    Hiding the widgets in CSS is not enough on its own.

    The stylesheet rule carries !important, which beats a vendor's ordinary inline
    style. It does NOT beat an inline `display: block !important`, and UserWay is
    known to write !important inline -- it is what defeated the transform approach
    earlier on this ticket. Proven rather than assumed: with the keyboard class set,
    an element carrying inline `display:block !important` computes to `block` while
    every other widget computes to `none`.

    That matters most for a widget that arrives DURING a keyboard session, which is
    the reported case: tap a field before the widgets have loaded, and they appear
    on top of the keyboard.

    So the class stays the primary mechanism and this enforces it, on the same clock
    as the position reconciler. Only what we set is ever removed, so a vendor's own
    display value survives us.
  */
  function enforceKeyboardHiding() {
    var open = document.documentElement.classList.contains('olf-aib-keyboard-open');
    for (var i = 0; i < COMPANION_SELECTORS.length; i++) {
      var el = document.querySelector(COMPANION_SELECTORS[i]);
      if (!el) continue;
      if (open) {
        if (el.style.display !== 'none') {
          el.__olfHidPrev = el.style.display;          // remember, do not invent
          el.style.setProperty('display', 'none', 'important');
          el.__olfHidden = true;
        }
      } else if (el.__olfHidden) {
        el.style.removeProperty('display');
        if (el.__olfHidPrev) el.style.display = el.__olfHidPrev;
        el.__olfHidden = false;
      }
    }
  }

  function reconcile() {
    enforceKeyboardHiding();
    for (var i = shifted.length - 1; i >= 0; i--) {
      var el = shifted[i];
      if (!el.isConnected) { shifted.splice(i, 1); continue; }   // vendor removed it
      if (el.style.bottom === el.__olfShiftWanted) continue;
      el.__olfShiftCount = 0;        // a fresh opportunity, not a continued burst
      el.__olfShiftTotal = (el.__olfShiftTotal || 0) + 1;
      el.style.setProperty('bottom', el.__olfShiftWanted, 'important');
    }
    if (!shifted.length && !document.documentElement.classList.contains('olf-aib-keyboard-open')) {
      clearInterval(reconciler); reconciler = null;
    }
  }

  function startReconciler() {
    if (!reconciler) reconciler = setInterval(reconcile, RECONCILE_MS);
  }

  function applyShift(el, liftPx) {
    var value = liftPx.toFixed(1) + 'px';
    el.__olfShiftY = liftPx;
    el.__olfShiftWanted = value;
    el.style.setProperty('bottom', value, 'important');

    if (el.__olfShiftObserver) return;
    el.__olfShiftCount = 0;
    var observer = new MutationObserver(function () {
      if (el.style.bottom === el.__olfShiftWanted) return;      // ours, nothing to do
      var now = Date.now();
      if (now - (el.__olfShiftLast || 0) > SHIFT_BURST_QUIET_MS) el.__olfShiftCount = 0;
      el.__olfShiftLast = now;
      if (el.__olfShiftCount >= SHIFT_BURST_LIMIT) return;      // back off; stay connected
      el.__olfShiftCount += 1;
      el.__olfShiftTotal = (el.__olfShiftTotal || 0) + 1;
      el.style.setProperty('bottom', el.__olfShiftWanted, 'important');
    });
    observer.observe(el, { attributes: true, attributeFilter: ['style'] });
    el.__olfShiftObserver = observer;
    shifted.push(el);
    startReconciler();
  }

  /*
    Let the note box be as tall as the note.

    The textarea has a fixed height, so on a phone a customer saw about 151px of a
    note that needed 563px and had to scroll INSIDE the textarea, nested in a page
    that also scrolls, with nothing to indicate the text continued. The note is the
    thing they are being asked to approve, so hiding three quarters of it works
    against the step.

    This used to be phones only, on the grounds that the desktop column was wide
    enough. That stopped being true when the note limit went to 250 (KAN-813): a
    237-character note ran to five lines in a three-line box at 1440, and Hannah had
    to drag the corner to read it (KAN-839, Slack 2026-10-21).
  */
  OlfAiBuilder.prototype.autoSizeNote = function () {
    this.autoSize(this.el('noteText'));
    this.autoSize(this.el('story'));
  };

  /*
    Grow a textarea to its content, never below the height its `rows` give it.

    The story field had the same problem as the note on phones: with a realistic
    story typed, 208px of text sat in a 135px box, so 73px was hidden WHILE THE
    CUSTOMER WAS STILL TYPING.

    The `rows` height is the floor so a short note keeps the box the mockup draws.
    A hidden textarea measures 0, so it is left alone and sized when it is shown.
  */
  OlfAiBuilder.prototype.autoSize = function (ta) {
    if (!ta) return;
    ta.style.removeProperty('height');      // back to the `rows` height, or it only ever grows
    ta.style.removeProperty('overflow-y');
    if (!ta.offsetParent) return;
    var floor = ta.offsetHeight;
    var needed = ta.scrollHeight + (ta.offsetHeight - ta.clientHeight);   // + borders
    if (needed > floor) {
      ta.style.height = needed + 'px';
      ta.style.overflowY = 'hidden';
    }
  };

  OlfAiBuilder.prototype.fieldValues = function () {
    var out = {};
    ['recipient', 'story', 'bottleNameInput', 'fromName', 'toName', 'noteText', 'recipientEmail', 'noteGreeting', 'noteSignoff'].forEach(function (k) {
      if (this.el(k)) out[k] = this.el(k).value;
    }, this);
    return out;
  };

  OlfAiBuilder.prototype.saveSession = function () {
    try {
      // Nothing worth restoring until a blend exists; an empty form is not progress.
      // Nor once Start over has run: a save queued by an earlier click would put the
      // blend straight back before the fresh page arrives (KAN-842).
      if (!this.state.blend || this._startingOver) return;
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        v: SESSION_VERSION,
        fields: this.fieldValues(),
        state: {
          blend: this.state.blend,
          mode: this.state.mode,
          intense: this.state.intense,
          colorName: this.state.colorName,
          colorHex: this.state.colorHex,
          note: this.state.note
        }
      }));
    } catch (e) {
      // Private mode or storage disabled. Composing still works; only the restore is lost.
    }
  };

  OlfAiBuilder.prototype.clearSession = function () {
    try { window.sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
  };

  /*
    Start over (KAN-842). A refresh deliberately restores the work (KAN-536), so without
    this the only way to a clean page was closing the tab.

    Clearing the saved draft and loading the page afresh is the whole reset: every field,
    the bottle preview, the note and the totals come back exactly as a first visit draws
    them, with nothing left half-cleared. A replace navigation rather than reload(),
    because a reload can refill form fields from the browser's own form cache. The
    per-visit regeneration id (sessionId) is kept: starting over and composing again is
    the same visit.
  */
  /*
    The Start over button asks first (KAN-842, the brand's Option A). A blend costs an AI call
    and cannot be made again the same way, so one stray tap must not throw it away. The box
    says what goes: the recipient's story, the blend by name, the bottle and the note.
  */
  OlfAiBuilder.prototype.bindStartOverAsk = function () {
    var self = this;
    var ask = this.el('startOverAsk');
    var dialog = this.el('startOverDialog');
    if (!ask || !dialog || typeof dialog.showModal !== 'function') return;
    ask.addEventListener('click', function () {
      var body = self.el('startOverBody');
      if (body) body.textContent = self.startOverWords(body.getAttribute('data-template') || '');
      dialog.showModal();
    });
    if (this.el('startOverKeep')) this.el('startOverKeep').addEventListener('click', function () { dialog.close(); });
    // A tap outside the card is a "keep it".
    dialog.addEventListener('click', function (e) {
      if (e.target !== dialog) return;
      var r = dialog.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
    });
    // Esc, Keep or the backdrop: focus goes back to the button that opened the box.
    dialog.addEventListener('close', function () { ask.focus(); });
  };

  OlfAiBuilder.prototype.startOverWords = function (template) {
    var who = (this.el('recipient') && this.el('recipient').value || '').trim();
    var blend = (this.state.blend && this.state.blend.name) || '';
    return template
      .replace(/\[story\]/g, who ? who + '\u2019s story' : 'your story')
      .replace(/\[blend\] ?/g, blend ? blend + ' ' : '')
      .replace(/'/g, '\u2019');
  };

  OlfAiBuilder.prototype.startOver = function () {
    this._startingOver = true;
    this.clearSession();
    try { window.history.scrollRestoration = 'auto'; } catch (e) {}
    window.location.replace(window.location.pathname + window.location.search);
  };

  OlfAiBuilder.prototype.restoreSession = function () {
    var saved;
    try {
      var raw = window.sessionStorage.getItem(SESSION_KEY);
      if (!raw) return false;
      saved = JSON.parse(raw);
    } catch (e) { return false; }

    if (!saved || saved.v !== SESSION_VERSION || !saved.state || !saved.state.blend) {
      this.clearSession();
      return false;
    }

    // Fields first: presentBlend() only fills the bottle name and colour when they
    // are still empty, so restoring beforehand keeps the customer's own choices.
    var fields = saved.fields || {};
    Object.keys(fields).forEach(function (k) {
      if (this.el(k)) this.el(k).value = fields[k];
    }, this);

    // Restore the saved mode before normalizing, including sessions written
    // before this field had a character policy.
    this.state.mode = saved.state.mode === 'engrave' ? 'engrave' : 'label';
    this.syncBottleName();

    this.state.blend = saved.state.blend;
    this.state.note = saved.state.note || '';
    this.state.colorName = saved.state.colorName || '';
    this.state.colorHex = saved.state.colorHex || '';

    // KAN-681 -- a session saved before the limit was lowered can carry a longer note
    // than the field now allows, and restoring it raw would put it back over the cap.
    this.setNote(this.el('noteText') ? this.el('noteText').value : this.state.note);

    this.presentBlend({ restoring: true });
    /*
      KAN-842 -- reload at the top, answers filled in. Hannah (Slack, 22 Oct) refreshed to
      start over and the page dropped her straight back onto the old blend. The browser's own
      scroll restoration did that as much as presentBlend did, so it is switched off here,
      only when there is something to restore.
    */
    try { window.history.scrollRestoration = 'manual'; } catch (e) {}
    window.scrollTo(0, 0);

    // Re-apply through the setters so the pressed states and totals match, rather
    // than assigning to state and leaving the buttons looking unselected.
    this.setMode(saved.state.mode || 'label');
    this.setIntense(!!saved.state.intense);
    if (saved.state.colorName) this.setColor(saved.state.colorName, saved.state.colorHex);
    // A restored greeting or sign-off that differs from the automatic text was typed by
    // the shopper, so it stays theirs.
    var auto = this.autoLetter();
    this.greetEdited = !!(this.el('noteGreeting') && fields.noteGreeting != null && fields.noteGreeting !== auto.greeting);
    this.signEdited = !!(this.el('noteSignoff') && fields.noteSignoff != null && fields.noteSignoff !== auto.signoff);
    this.renderLetter();
    this.autoSizeNote();
    return true;
  };

  OlfAiBuilder.prototype.remoteCompose = function (endpoint, story, recipient) {
    var self = this;
    var ctl = new AbortController();
    var t = setTimeout(function () { ctl.abort(); }, 25000);
    return fetch(endpoint.replace(/\/$/, '') + '/compose', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: story,
        recipientName: recipient,
        sessionId: this.sessionId(),
        // Attempt 2+ in one visit IS the regeneration signal the brand asked for.
        attempt: this.state.attempt || 1
      }),
      signal: ctl.signal
    }).then(function (r) {
      clearTimeout(t);
      // 422 = the story was too thin to build from. That is not a failure the
      // customer should see as "something went wrong" — the service tells us
      // what would help, and they can act on it.
      if (r.status === 422) {
        return r.json().then(function (d) {
          var e = new Error(d.error || 'tell us a little more');
          e.sparse = true;
          e.playful = Boolean(d.playful);
          e.questions = Array.isArray(d.questions) ? d.questions : [];
          throw e;
        });
      }
      if (!r.ok) throw new Error('compose http ' + r.status);
      return r.json();
    }).then(function (data) {
      var resolved = self.resolveBlend(data, story);
      if (!resolved) throw new Error('unresolvable blend');
      return resolved;
    });
  };

  OlfAiBuilder.prototype.availableCores = function () {
    return (this.library.cores || []).filter(function (c) {
      return (c.variants || []).some(function (v) { return v.available; });
    });
  };

  OlfAiBuilder.prototype.resolveBlend = function (data, story) {
    var cores = this.availableCores();
    if (!cores.length) return null;
    var coreKey = String(data.core || data.coreName || data.coreHandle || '').toLowerCase();
    var core = cores.find(function (c) { return c.name.toLowerCase() === coreKey || c.handle === coreKey; })
      || cores.find(function (c) { return coreKey.indexOf(c.name.toLowerCase()) !== -1; });
    if (!core) return null;

    var variants = core.variants.filter(function (v) { return v.available; });
    var variant = null;
    var wantNum = parseInt(data.accordNumber, 10);
    if (!isNaN(wantNum)) {
      variant = variants.find(function (v) { return accordNumberFromSku(v.sku) === wantNum; });
    }
    if (!variant && data.accord) {
      var ak = String(data.accord).toLowerCase();
      variant = variants.find(function (v) { return v.accord.toLowerCase() === ak; })
        || variants.find(function (v) { return v.accord.toLowerCase().indexOf(ak) !== -1 || ak.indexOf(v.accord.toLowerCase()) !== -1; });
    }
    if (!variant) return null;

    return {
      demo: false,
      core: core,
      variant: variant,
      // Join key back to the stored composition. The service mints it at
      // /compose — before an order exists — so it rides the cart line into the
      // order, where a webhook backfills the order id onto that row.
      compositionId: String(data.compositionId || ''),
      name: String(data.blendName || core.name).slice(0, 40),
      family: String(data.family || ''),
      /*
        Six, at the brand's request (KAN-632). The service also caps at six, and
        this is the second of the two places that number was written down -- the
        page truncated to three regardless of what the service returned, so
        raising it there alone would have shipped as done and still shown three.
      */
      keyNotes: Array.isArray(data.keyNotes) ? data.keyNotes.slice(0, MAX_KEY_NOTES) : [],
      tagline: String(data.tagline || ''),
      story: String(data.story || ''),
      // Why this blend was chosen, quoting the customer's own words. Carried so
      // the gift note can be written from the reasoning instead of from the raw
      // story — otherwise the note and the blend drift apart.
      reasoning: (data.reasoning && typeof data.reasoning === 'object') ? data.reasoning : null,
      // Echoed back to /note so Stage D can change register when the story
      // involves bereavement or illness. A single enum value, not the story.
      sensitivity: String(data.sensitivity || 'none'),
      /*
        The service shed load and returned a blend it did not compose with a
        model. It must NOT be presented as a personal recommendation — the
        customer is spending money and is owed the truth about what this is.
      */
      degraded: Boolean(data.degraded),
      degradedMessage: String(data.degradedMessage || '')
    };
  };

  OlfAiBuilder.prototype.demoCompose = function (story) {
    var cores = this.availableCores();
    if (!cores.length) return null;
    var h = hashString(story);
    var core = cores[h % cores.length];
    var variants = core.variants.filter(function (v) { return v.available; });
    var variant = variants[(h >> 4) % variants.length];
    // Same cap as the composed path. KAN-632 raised the AI blend from three notes to
    // six but left this fallback at three, so a demo blend contradicted the brand's
    // own request whenever the service was unavailable.
    var keyNotes = String(variant.accord).split(/[\/,&]| and /i)
      .map(function (s) { return s.trim(); })
      .filter(Boolean)
      .slice(0, MAX_KEY_NOTES);
    return {
      demo: true,
      core: core,
      variant: variant,
      name: DEMO_NAMES[h % DEMO_NAMES.length],
      family: core.name + ' × ' + variant.accord,
      keyNotes: keyNotes,
      tagline: DEMO_TAGLINES[(h >> 2) % DEMO_TAGLINES.length],
      story: ''
    };
  };

  /* ---------- presentation ---------- */

  // opts.restoring: a refresh is putting back a blend the customer already saw, so the page
  // stays where it is instead of scrolling down to the result (KAN-842).
  OlfAiBuilder.prototype.presentBlend = function (opts) {
    var b = this.state.blend;
    if (!b) return;

    if (this.el('blendName')) this.el('blendName').textContent = b.name;
    // KAN-842 -- nothing to start over until a blend exists.
    if (this.el('startOverAsk')) this.el('startOverAsk').hidden = false;
    if (this.el('blendFamily')) this.el('blendFamily').textContent = b.family;
    if (this.el('tagline')) this.el('tagline').textContent = b.tagline;
    // KAN-774 -- the AI's one-line story is not shown any more. b.story stays on the blend
    // because the gift-note writer reads it ("how we described it"); it is just never drawn.
    if (this.el('keyNotes')) {
      this.el('keyNotes').innerHTML = '';
      var frag = document.createDocumentFragment();
      b.keyNotes.forEach(function (n) {
        var pill = document.createElement('span');
        pill.className = 'olf-aib__note-pill';
        pill.textContent = n;
        frag.appendChild(pill);
      });
      this.el('keyNotes').appendChild(frag);
    }

    // default bottle name: the recipient's FIRST name, else the blend name's first word.
    // Hannah, call of 2026-09-22 (20:04-20:29): "Michael Jordan" was filling the 9-char
    // label as "Michael J" -- the first nine characters, not a name. First word only.
    var recipient = (this.el('recipient') && this.el('recipient').value || '').trim();
    var firstWord = function (v) { return String(v || '').trim().split(/\s+/)[0] || ''; };
    if (this.el('bottleNameInput') && !this.el('bottleNameInput').value) {
      this.el('bottleNameInput').value = normalizeBottleName(firstWord(recipient) || firstWord(b.name), this.state.mode);
    }

    // default label color: nearest swatch to the core's accent
    if (!this.state.colorName) {
      var swatches = Array.prototype.slice.call(this.querySelectorAll('[data-aib-color]'));
      if (swatches.length) {
        var best = swatches[0];
        var bestD = Infinity;
        swatches.forEach(function (sw) {
          var d = hexDist(sw.getAttribute('data-aib-hex'), b.core.accent);
          if (d < bestD) { bestD = d; best = sw; }
        });
        this.setColor(best.getAttribute('data-aib-color'), best.getAttribute('data-aib-hex'));
      }
    }

    // KAN-676 -- 'reviews' reveals with the steps so the row is never stranded in the gap
    // the hidden cards will fill. KAN-774 -- 'inlineCta' is the Add to Cart under step 3;
    // the old step 4 (shipping choice) is gone.
    ['reveal', 'stepPersonalize', 'stepNote', 'inlineCta', 'reviews', 'bar'].forEach(function (k) {
      if (this.el(k)) this.el(k).hidden = false;
    }, this);

    this.renderBottle();
    this.renderTotals();
    this.saveSession();

    // keep the sticky bar from covering the last card
    if (this.el('bar')) {
      var barH = this.el('bar').offsetHeight;
      this.style.paddingBottom = (barH + 24) + 'px';
      /*
        The third-party widgets -- UserWay, Gorgias, the SMS button -- are injected
        outside this section and anchor themselves to the viewport bottom, where they
        land on top of ADD TO CART. On mobile the accessibility button covers it
        outright, and the bar is the only route to checkout on this page.

        Publish the bar's height and a flag on <html>; the stylesheet lifts each
        widget by that much. Both only exist while the bar does, and this section only
        renders on the builder page, so nothing moves anywhere else on the site.
      */
      document.documentElement.style.setProperty('--olf-aib-bar-h', barH + 'px');
      document.documentElement.classList.add('olf-aib-bar-open');

      /*
        These widgets load asynchronously and some re-render themselves.

        Fixed retries alone left a visible gap: watched on a real iPhone, the
        accessibility button sat on top of ADD TO CART for several seconds after
        load and only jumped into line when a retry happened to fire. Watching for
        the insertion instead means each widget is placed as it appears.

        The retries stay as a backstop for widgets that mutate in place rather than
        being inserted, and the observer stops once the page has settled so it is
        not live for the whole visit.
      */
      var self2 = this;
      self2.positionCompanions(false, 'reveal');
      // a relative correction converges in two passes; the first moves it, the
      // second confirms or trims the remainder
      window.requestAnimationFrame(function () { self2.positionCompanions(true, 'converge'); });
      [400, 1200, 3000].forEach(function (ms) {
        setTimeout(function () { self2.positionCompanions(true, 'retry ' + ms + 'ms'); }, ms);
      });

      if (!this._companionObserver) {
        var settle = null;
        var observer = new MutationObserver(function (records) {
          /*
            Only when a COMPANION appears -- not on any insertion anywhere.

            This used to fire for every element added to the page and force a
            reposition. Scrolling lazy-loads content, so scrolling inserted
            elements, which repositioned every widget: the shifting reported on
            device after the scroll handler was already removed.
          */
          var sawCompanion = records.some(function (r) {
            return Array.prototype.some.call(r.addedNodes, function (n) {
              if (n.nodeType !== 1) return false;
              return COMPANION_SELECTORS.some(function (sel) {
                try { return n.matches(sel) || n.querySelector(sel); } catch (e) { return false; }
              });
            });
          });
          if (!sawCompanion) return;
          // coalesce a burst of insertions into one pass
          if (settle) clearTimeout(settle);
          settle = setTimeout(function () { self2.positionCompanions(true, 'companion inserted'); }, 60);
        });
        observer.observe(document.body, { childList: true, subtree: true });
        this._companionObserver = observer;
        // widgets that have not loaded within 15s are not going to
        setTimeout(function () { observer.disconnect(); }, 15000);
      }
      if (!this._companionResize) {
        // one pass per animation frame at most, however many events arrive
        var pending = false;
        this._companionResize = function () {
          if (pending) return;
          pending = true;
          window.requestAnimationFrame(function () {
            pending = false;
            self2.positionCompanions(false, 'resize');
            self2.autoSizeNote();
          });
        };
        window.addEventListener('resize', this._companionResize);
        window.addEventListener('orientationchange', function () {
          // rotation changes the width, so the guard has to be bypassed
          self2.positionCompanions(true, 'orientation');
          self2.autoSizeNote();
        });
      }
    }

    if (!(opts && opts.restoring) && this.el('reveal') && this.el('reveal').scrollIntoView) {
      this.el('reveal').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /*
    KAN-683. The bottle photographs, keyed by the label colour's own name, parsed once
    from the markup. Returns an empty map if the attribute is missing or malformed, so a
    bad edit degrades to the fallback photograph rather than throwing inside renderBottle.
  */
  OlfAiBuilder.prototype.bottlePhotos = function () {
    if (this._bottlePhotos) return this._bottlePhotos;
    var host = this.el('bottlePhoto');
    var raw = host && host.getAttribute('data-aib-photos');
    try { this._bottlePhotos = raw ? JSON.parse(raw) : {}; }
    catch (e) { this._bottlePhotos = {}; }
    return this._bottlePhotos;
  };

  OlfAiBuilder.prototype.renderBottle = function () {
    var b = this.state.blend;
    if (!b) return;
    var name = (this.el('bottleNameInput') && this.el('bottleNameInput').value || '').trim() || b.name;
    var mono = (b.name.replace(/[^a-z]/gi, '').slice(0, 2) || 'CB');
    mono = mono.charAt(0).toUpperCase() + (mono.charAt(1) || '').toLowerCase();
    if (this.el('monogram')) this.el('monogram').textContent = mono;
    if (this.el('bottleName')) this.el('bottleName').textContent = name;
    var hex = this.state.mode === 'engrave' ? b.core.accent : (this.state.colorHex || b.core.accent);
    this.style.setProperty('--aib-blend-accent', hex);

    // the photograph, and the name stamped onto its label
    var isEngrave = this.state.mode === 'engrave';
    var photos = this.bottlePhotos();
    var img = this.el('bottleImg');
    if (img) {
      var url = isEngrave ? photos._engraved : photos[this.state.colorName];
      if (!url) url = photos._fallback;
      // compare against the resolved src, or a relative match re-sets it on every keystroke
      if (url && img.src !== url) {
        img.onerror = function () { if (photos._fallback && img.src !== photos._fallback) img.src = photos._fallback; };
        img.src = url;
      }
      var alt = isEngrave ? 'Bottle engraved with ' : 'Bottle labelled ';
      img.setAttribute('alt', name ? alt + name : 'Your custom bottle');
    }
    var stamp = this.el('bottleStamp');
    if (stamp) {
      stamp.textContent = isEngrave ? '' : name;
      stamp.hidden = isEngrave || !name;
    }
    var engraving = this.el('bottleEngraving');
    if (engraving) {
      engraving.textContent = isEngrave ? name : '';
      engraving.hidden = !isEngrave || !name;
    }
  };

  OlfAiBuilder.prototype.renderTotals = function () {
    var b = this.state.blend;
    if (!b) return;
    var total = b.variant.price;
    if (this.state.mode === 'engrave') total += this.engraveFeeCents();
    if (this.state.intense && this.config.intense && this.config.intense.enabled !== false) total += this.config.intense.price || 0;
    if (this.el('price')) this.el('price').textContent = fmtMoney(total);
    if (this.el('barTotal')) this.el('barTotal').textContent = fmtMoney(total);
    if (this.el('barName')) this.el('barName').textContent = b.name + ' · Custom Blend';
  };

  OlfAiBuilder.prototype.setMode = function (mode) {
    this.state.mode = mode;
    var nameInput = this.el('bottleNameInput');
    if (nameInput) {
      nameInput.setAttribute('pattern', mode === 'engrave' ? '[\\x20-\\x7E]+' : '[A-Za-z0-9 .]+');
      this.syncBottleName();
    }
    // The same guidance the Gift Bottle and the customizer show under this field,
    // swapped with the mode. Hannah approved straying from the mockup for it on the
    // 2026-09-22 call ("Yeah, yeah, yeah, totally").
    var help = this.el('bottleNameHelp');
    if (help) {
      help.textContent = help.getAttribute(mode === 'engrave' ? 'data-engrave-help' : 'data-label-help') || '';
    }
    if (this.el('modeLabel')) this.el('modeLabel').setAttribute('aria-pressed', String(mode === 'label'));
    if (this.el('modeEngrave')) this.el('modeEngrave').setAttribute('aria-pressed', String(mode === 'engrave'));
    if (this.el('colorField')) this.el('colorField').hidden = mode !== 'label';
    if (this.el('engraveNote')) this.el('engraveNote').hidden = mode !== 'engrave';
    this.renderBottle();
    this.renderTotals();
  };

  OlfAiBuilder.prototype.setIntense = function (on) {
    this.state.intense = on;
    if (this.el('intenseToggle')) this.el('intenseToggle').setAttribute('aria-pressed', String(on));
    if (this.el('intenseDesc')) this.el('intenseDesc').hidden = !on;
    this.renderTotals();
  };

  OlfAiBuilder.prototype.setColor = function (name, hex) {
    this.state.colorName = name;
    this.state.colorHex = hex;
    this.querySelectorAll('[data-aib-color]').forEach(function (sw) {
      sw.setAttribute('aria-pressed', String(sw.getAttribute('data-aib-color') === name));
    });
    this.renderBottle();
  };

  /*
    Tell the service when the page fails (KAN-560).

    Everything below this line used to happen in silence. A customer saw an error
    and we learned nothing, and add-to-cart cannot be seen from the server at all
    because it posts to Shopify's /cart/add.js rather than to us -- so the step
    that takes the money was the least observable thing on the page.

    Enums only, no free text. The service accepts a stage and a kind and nothing
    else, so a story, a note or a name cannot travel with a report even by
    accident. That is enforced there, not trusted here.

    Every failure mode of the reporter itself is swallowed: a page that is already
    failing must not also throw from the thing telling us it failed.
  */
  OlfAiBuilder.prototype.reportFailure = function (stage, err) {
    try {
      var endpoint = (this.config.endpoint || '').trim();
      if (!endpoint) return;                 // demo mode: nothing to report to
      fetch(endpoint.replace(/\/$/, '') + '/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stage: stage,
          kind: failureKind(err),
          status: failureStatus(err)
        }),
        // the report should still leave if the customer navigates away
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* reporting must never break the page */ }
  };

  function failureKind(err) {
    if (!err) return 'unknown';
    if (err.name === 'AbortError') return 'timeout';
    if (err.httpStatus) return 'http';
    var m = String(err.message || '');
    if (/http \d{3}/i.test(m)) return 'http';
    // Chrome, Firefox and Safari each word a blocked or offline request differently.
    if (/failed to fetch|networkerror|load failed|network request failed/i.test(m)) return 'network';
    if (/json|unexpected token/i.test(m)) return 'parse';
    return 'unknown';
  }

  function failureStatus(err) {
    if (err && err.httpStatus) return Number(err.httpStatus) || 0;
    var m = String(err && err.message || '').match(/http (\d{3})/i);
    return m ? Number(m[1]) : 0;
  }

  /* ---------- gift note ---------- */

  OlfAiBuilder.prototype.writeNote = function () {
    var self = this;
    var b = this.state.blend;
    if (!b || this.state.noteWriting) return;
    this.state.noteWriting = true;
    if (this.el('noteWriting')) this.el('noteWriting').hidden = false;
    if (this.el('noteText')) this.el('noteText').hidden = true;

    var endpoint = (this.config.endpoint || '').trim();
    var story = (this.el('story') && this.el('story').value || '').trim();
    var run;
    if (endpoint) {
      var ctl = new AbortController();
      var t = setTimeout(function () { ctl.abort(); }, 20000);
      run = fetch(endpoint.replace(/\/$/, '') + '/note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Sent only as a fallback for a service that predates the staged
          // pipeline. When `reasoning` is present the service writes the note
          // from it and ignores this.
          prompt: story,
          // KAN-681 -- the brand sets this cap (100 as of 2026-09-22). Without it the
          // service writes 2-4 sentences and every draft is trimmed after the fact.
          maxChars: self.noteLimit() || undefined,
          // KAN-813 -- the note must open with these words. The greeting and sign-off are
          // added around it here, so the service is told not to write either.
          opening: (self.config.letter && self.config.letter.opening) || undefined,
          blendName: b.name,
          family: b.family,
          keyNotes: b.keyNotes,
          reasoning: b.reasoning ? {
            coreReason: b.reasoning.coreReason || '',
            coreQuote: b.reasoning.coreQuote || '',
            accordReason: b.reasoning.accordReason || '',
            accordQuote: b.reasoning.accordQuote || '',
            story: b.story || ''
          } : null,
          sensitivity: b.sensitivity || 'none',
          from: this.el('fromName') && this.el('fromName').value || '',
          to: this.el('toName') && this.el('toName').value || ''
        }),
        signal: ctl.signal
      }).then(function (r) {
        clearTimeout(t);
        if (!r.ok) throw new Error('note http ' + r.status);
        return r.json();
      }).then(function (d) { return String(d.note || '').trim(); });
    } else {
      run = Promise.resolve(this.localNote(b));
    }

    run.then(function (note) {
      self.state.noteWriting = false;
      self.setNote(note);
      if (self.el('noteText')) {
        self.el('noteText').hidden = false;
        self.autoSizeNote();
      }
      if (self.el('noteWriting')) self.el('noteWriting').hidden = true;
    }).catch(function (err) {
      self.reportFailure('note', err);
      /*
        Never hand back an empty box.

        This used to reveal `noteText` with whatever was in it, which after a failed
        request is nothing. The customer landed on a step headed "we'll write it, you
        tweak it" holding a blank textarea, with no error and no explanation, and
        nobody was told -- see KAN-560 for the reporting half of this.

        The written-here note already existed for the no-endpoint demo; it just was
        not reachable from the failure path. A note they can edit is a far better
        outcome than an empty box, and it keeps the step's promise.
      */
      self.state.noteWriting = false;
      self.state.note = self.localNote(b);
      if (self.el('noteText')) {
        if (!self.el('noteText').value.trim()) self.setNote(self.state.note);
        self.el('noteText').hidden = false;
        self.autoSizeNote();
      }
      if (self.el('noteWriting')) self.el('noteWriting').hidden = true;
    });
  };

  /* KAN-681 -- the note is capped at a number the brand sets (200, confirmed by
     Olfactory NYC on 2026-09-16).

     `maxlength` only constrains TYPING. The note is usually written by the service and
     assigned straight to .value, which maxlength does not touch, so a long one would sit
     there over the limit and reach the cart. Everything that sets the note goes through
     setNote() instead. */
  /* KAN-839 -- the cap is for the whole card: greeting, note and sign-off together.

     It used to apply to the note alone, while "Dear …," and "Yours, …" took up to 60
     characters each on top, so a card the counter called 235 / 250 could run to well over
     300 (Cooper, 21 Oct: "Yours, Cooperasdasd…"). The brand's number is still the
     textarea's `maxlength` in the markup; it is read once, because the maxlengths below
     are then adjusted to share it out. */
  OlfAiBuilder.prototype.cardLimit = function () {
    if (this._cardLimit == null) {
      var el = this.el('noteText');
      var n = el && parseInt(el.getAttribute('maxlength'), 10);
      this._cardLimit = n > 0 ? n : 0;
    }
    return this._cardLimit;
  };

  // The three parts of the card in order; a missing field counts as empty.
  OlfAiBuilder.prototype.cardFields = function () {
    var self = this;
    return ['noteGreeting', 'noteText', 'noteSignoff'].map(function (k) { return self.el(k); }).filter(Boolean);
  };

  OlfAiBuilder.prototype.cardLength = function () {
    return this.cardFields().reduce(function (n, f) { return n + f.value.length; }, 0);
  };

  // What the NOTE may use: the card's cap less the greeting and sign-off. The AI is asked
  // to write to this, so its draft fits beside whatever names are already in the letter.
  OlfAiBuilder.prototype.noteLimit = function () {
    var cap = this.cardLimit();
    if (!cap) return 0;
    var note = this.el('noteText');
    return Math.max(0, cap - (this.cardLength() - (note ? note.value.length : 0)));
  };

  /* Share out what is left of the cap: each field may grow by the characters remaining,
     and never past its own markup limit (60 for the greeting and sign-off). A field already
     over its share keeps its text and simply accepts no more until something is deleted. */
  OlfAiBuilder.prototype.applyCardLimit = function () {
    var cap = this.cardLimit();
    if (!cap) return;
    var left = Math.max(0, cap - this.cardLength());
    this.cardFields().forEach(function (f) {
      if (f.__olfOwnMax == null) f.__olfOwnMax = parseInt(f.getAttribute('maxlength'), 10) || cap;
      f.setAttribute('maxlength', String(Math.max(f.value.length, Math.min(f.__olfOwnMax, f.value.length + left))));
    });
  };

  OlfAiBuilder.prototype.cardOver = function () {
    var cap = this.cardLimit();
    return cap > 0 && this.cardLength() > cap;
  };

  OlfAiBuilder.prototype.setNote = function (text) {
    var el = this.el('noteText');
    var limit = this.noteLimit();
    var value = fitNote(text, limit);
    if (el) el.value = value;
    this.state.note = value;
    this.renderNoteCount();
    return value;
  };

  OlfAiBuilder.prototype.renderNoteCount = function () {
    var out = this.el('noteCount');
    if (!out) return;
    var cap = this.cardLimit();
    if (!cap) { out.textContent = ''; return; }
    this.applyCardLimit();
    /* Typing cannot pass the cap, but filling To or From after the note is full can: the
       greeting and sign-off follow those fields. Say so rather than cut their words. */
    var over = this.cardOver();
    out.textContent = this.cardLength() + ' / ' + cap + (over ? ' — shorten the note to fit the card' : '');
    out.classList.toggle('is-over', over);
    var note = this.el('noteText');
    if (note && !over) note.setCustomValidity('');
  };


  /* A note written here rather than by the service: the demo path, and the fallback
     whenever /note fails. */
  OlfAiBuilder.prototype.localNote = function (b) {
    // KAN-813 -- a letter body: no name (the greeting carries it), the brand's opening first.
    var opening = (this.config.letter && this.config.letter.opening) || 'I made this scent for you because';
    var notes = (b.keyNotes || []).slice(0, 3).join(', ').toLowerCase();
    return opening + ' no one else should smell quite like you' + (notes ? ': ' + notes + '.' : '.');
  };

  /* ---------- cart ---------- */

  OlfAiBuilder.prototype.setAdding = function (on) {
    this.state.adding = on;
    this.all('addToCart').forEach(function (btn) { btn.disabled = on; });
    this.all('addIdle').forEach(function (n) { n.hidden = on; });
    this.all('addBusy').forEach(function (n) { n.hidden = !on; });
  };

  OlfAiBuilder.prototype.addToCart = function (srcBtn) {
    var self = this;
    var b = this.state.blend;
    if (!b || this.state.added || this.state.adding) return;

    var bottleName = this.syncBottleName().trim();
    if (!bottleName) {
      if (this.el('bottleNameInput')) {
        this.el('bottleNameInput').setCustomValidity('Enter at least one character other than a space.');
        this.el('bottleNameInput').reportValidity();
      }
      return;
    }
    // KAN-813 -- what is written on the card: "Dear [To]," + the note + "Yours, [From]".
    var note = this.letterText();
    // KAN-839 -- a card longer than the cap does not fit; never send one to the studio.
    if (this.cardOver() && this.el('noteText')) {
      this.el('noteText').setCustomValidity('Shorten the note so the whole card fits in ' + this.cardLimit() + ' characters.');
      this.el('noteText').reportValidity();
      return;
    }
    var emailInput = this.el('recipientEmail');
    var recipientEmail = (emailInput && emailInput.value || '').trim();
    if (emailInput && recipientEmail && !emailInput.checkValidity()) {
      emailInput.reportValidity();
      return;
    }
    var fromName = (this.el('fromName') && this.el('fromName').value || '').trim();
    var toName = (this.el('toName') && this.el('toName').value || '').trim();
    var isEngrave = this.state.mode === 'engrave';
    var promoActive = this.promoActive();

    var props = {
      'Blend Name': b.name,
      'Fragrance Family': b.family || null,
      'Key Notes': b.keyNotes.join(', ') || null,
      'Personalization Type': isEngrave ? 'Engrave' : 'Label',
      'Engraving': bottleName || null,
      'Label Color': isEngrave ? null : (this.state.colorName || null),
      'Gift Message': note || null,
      'Gift From': fromName || null,
      'Gift To': toName || null,
      // Display copy on the order, the same property name the At-Home gift form uses.
      // The gift itself is the cart attribute written below.
      'Recipient email': recipientEmail || null,
      '_ai_scent_builder': '1',
      // Underscore-prefixed so Shopify hides it from the customer. This is the
      // only link between the order and the stored composition — /compose runs
      // before an order exists, so there is no order id to write at that point.
      // Empty in demo mode (no service call was made), and the trailing cleanup
      // below drops it rather than sending an empty property.
      '_composition_id': (b.compositionId || null)
    };
    if (this.state.intense) {
      props['Enhance Scent'] = 'Yes (+' + fmtMoney((this.config.intense && this.config.intense.price) || 0) + ')';
      props['_is_enhanced_scent'] = true;
      props['_enhanced_scent_product'] = {
        id: this.config.intense.variantId,
        price: this.config.intense.price
      };
    }
    Object.keys(props).forEach(function (k) { if (props[k] === null || props[k] === '') delete props[k]; });

    var items = [{ id: b.variant.id, quantity: 1, properties: props }];

    if (this.state.intense && this.config.intense && this.config.intense.enabled !== false && this.config.intense.variantId) {
      items.push({
        id: this.config.intense.variantId,
        quantity: 1,
        parent_id: b.variant.id,
        properties: { _is_child: true, _is_enhanced_scent: true, _parent_id: b.variant.id }
      });
    }

    if (isEngrave) {
      var engraveVariantId = promoActive
        ? Number((this.config.freeEngravingPromo || {}).freeVariantId)
        : (this.config.engrave || {}).variantId;
      if (!engraveVariantId) {
        this.cartError('Engraving is not configured yet — please choose the printed label.');
        return;
      }
      items.push({
        id: engraveVariantId,
        quantity: 1,
        parent_id: b.variant.id,
        properties: {
          _is_child: true,
          _is_engraving_fee: true,
          _is_free_engraving_promo: promoActive,
          _parent_id: b.variant.id
        }
      });
    }

    this.setAdding(true);

    var body = { items: items };
    var drawer = document.querySelector('cart-drawer');
    try {
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map(function (s) { return s.section || s.id; });
        body.sections_url = window.location.pathname;
      }
    } catch (e) { /* sections refresh is best-effort */ }

    /*
      KAN-813 -- a recipient email makes this a gift order. The store's gift handling
      reads the `_rf_gifting_options` CART ATTRIBUTE, not line-item properties: the
      olf-apps order webhook records the recipient from it, and when the parcel is
      marked delivered it sends the recipient Klaviyo's "MS | Order Delivered" email,
      whose custom-scent branch is "Your custom scent has arrived". Nothing is sent to
      the recipient before that, which is what the field's help text promises.

      Same payload as the At-Home gift form and the cart's Gift Options. `credit` is 0:
      the "Store Credit Calculator" Flow only credits the products in its own list, and
      a custom scent is not one of them, so a builder gift never carries store credit.
      Written BEFORE the add so the order is created with it; a failure here does not
      stop the purchase.
    */
    var giftWrite = recipientEmail
      ? fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { _rf_gifting_options: JSON.stringify({
            name: toName,
            email: recipientEmail,
            message: note,
            credit: 0,
            mode: 'direct'
          }) } })
        }).then(function (r) {
          if (!r.ok) throw new Error('gift attribute http ' + r.status);
        }).catch(function (err) { self.reportFailure('gift', err); })
      : Promise.resolve();

    giftWrite.then(function () {
      return fetch((self.config.cartAddUrl || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
    }).then(function (r) { return r.json(); }).then(function (response) {
      if (response.status && response.status !== 200) {
        // Carry Shopify's status so the report can say 422 rather than "unknown".
        throw Object.assign(
          new Error(response.description || response.message || 'Could not add to cart'),
          { httpStatus: response.status },
        );
      }
      self.setAdding(false);
      self.state.added = true;
      // In the cart now: keeping the draft would restore a blend they have already
      // bought and make the next visit look like an unfinished order.
      self.clearSession();
      self.all('addToCart').forEach(function (btn) { btn.hidden = true; });
      self.all('addedState').forEach(function (n) { n.hidden = false; });
      // The button that was pressed has just been hidden while it held keyboard focus, which
      // drops focus to <body>. Hand it to the "View cart" link beside it (same parent), and
      // do not scroll: the sticky bar's button must not jump the page up to the one under step 3.
      var viewCart = srcBtn && srcBtn.parentElement && srcBtn.parentElement.querySelector('[data-aib="addedState"] a');
      if (viewCart) { try { viewCart.focus({ preventScroll: true }); } catch (e) { viewCart.focus(); } }
      try {
        if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(response);
      } catch (e) { /* drawer refresh is best-effort */ }
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'olf-ai-builder' });
        }
      } catch (e) { /* pubsub is best-effort */ }
    }).catch(function (err) {
      self.setAdding(false);
      self.reportFailure('cart', err);
      self.cartError(err && err.message ? err.message : 'Could not add to cart — please try again.');
    });
  };

  OlfAiBuilder.prototype.cartError = function (msg) {
    if (this.el('composeError')) {
      this.el('composeError').textContent = msg;
      this.el('composeError').hidden = false;
      this.el('composeError').scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  customElements.define('olf-ai-builder', OlfAiBuilder);
})();
